import React, { useState } from 'react';
import { apiService } from '../../../../services/api';
import MaskedInput from '../../contacts/components/MaskedInput';
import LocationSelector from '../../contacts/components/LocationSelector';
import { CreateFornecedorData } from '../../../../types/fornecedor';
import { toast } from 'react-toastify';
import './QuickFornecedorForm.css';

interface QuickFornecedorFormProps {
  onSuccess: (newFornecedor: any) => void;
  onCancel: () => void;
}

const QuickFornecedorForm: React.FC<QuickFornecedorFormProps> = ({
  onSuccess,
  onCancel
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newFornecedor, setNewFornecedor] = useState<CreateFornecedorData>({
    id_conta: 2,
    nome: '',
    tipo_entidade: 'PJ',
    email: '',
    telefone: '',
    bairro: ''
  });

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Validações ANTES de setar loading
    if (newFornecedor.tipo_entidade === 'PF') {
      if (!newFornecedor.nome?.trim()) {
        toast.error('O nome é obrigatório para Pessoa Física');
        return;
      }
    } else {
      if (!newFornecedor.razao_social?.trim()) {
        toast.error('A razão social é obrigatória para Pessoa Jurídica');
        return;
      }
    }

    if (!newFornecedor.email?.trim()) {
      toast.error('O email é obrigatório');
      return;
    }

    if (!newFornecedor.telefone) {
      toast.error('O telefone é obrigatório');
      return;
    }

    try {
      setIsSubmitting(true);

      // Remove formatações dos campos antes de enviar
      const cleanData = {
        ...newFornecedor,
        telefone: newFornecedor.telefone?.replace(/\D/g, ''),
        whatsapp: newFornecedor.whatsapp?.replace(/\D/g, ''),
        cnpj: newFornecedor.cnpj?.replace(/\D/g, ''),
        cpf: newFornecedor.cpf?.replace(/\D/g, ''),
        cep: newFornecedor.cep?.replace(/\D/g, '')
      };

      // Preparar dados base
      const baseData = {
        id_conta: cleanData.id_conta,
        tipo_entidade: cleanData.tipo_entidade,
        nome: cleanData.tipo_entidade === 'PF' ? cleanData.nome?.trim() : cleanData.razao_social?.trim() || '',
        email: cleanData.email.trim(),
        telefone: cleanData.telefone,
        ...(cleanData.whatsapp && { whatsapp: cleanData.whatsapp }),
        ...(cleanData.endereco && { endereco: cleanData.endereco.trim() }),
        ...(cleanData.numero && { numero: cleanData.numero.trim() }),
        ...(cleanData.complemento && { complemento: cleanData.complemento.trim() }),
        ...(cleanData.cep && { cep: cleanData.cep }),
        ...(cleanData.estado && { estado: cleanData.estado }),
        ...(cleanData.id_municipio && { id_municipio: cleanData.id_municipio }),
        ...(cleanData.observacoes && { observacoes: cleanData.observacoes.trim() }),
        bairro: cleanData.bairro || ''
      };

      // Adicionar campos específicos do tipo
      const fornecedorData: CreateFornecedorData = {
        ...baseData,
        ...(cleanData.tipo_entidade === 'PF'
          ? { cpf: cleanData.cpf }
          : {
            razao_social: cleanData.razao_social?.trim(),
            nome_fantasia: cleanData.nome_fantasia?.trim(),
            cnpj: cleanData.cnpj,
          }
        )
      };

      const response = await apiService.createFornecedor(fornecedorData);

      if (response && response.data) {
        // Limpar form antes de chamar onSuccess
        setNewFornecedor({
          id_conta: 2,
          nome: '',
          tipo_entidade: 'PJ',
          email: '',
          telefone: '',
          bairro: ''
        });

        // Chamar callback de sucesso (o toast será mostrado no parent)
        onSuccess(response.data);
      }
    } catch (error) {
      console.error('Erro ao criar fornecedor:', error);
      toast.error('Erro ao criar fornecedor');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="quick-fornecedor-form-wrapper">
      <div className="alert alert-info d-flex align-items-center mb-3">
        <i className="ti ti-info-circle me-2"></i>
        <span>Cadastro rápido - Preencha os dados essenciais do fornecedor</span>
      </div>

      <div className="row">
        {/* Tipo de Pessoa */}
        <div className="col-12 mb-3">
          <label className="form-label mb-2">Tipo de Pessoa <span className="text-danger">*</span></label>
          <div className="d-flex gap-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="tipoEntidadeFornecedor"
                id="quickFornecedorPF"
                checked={newFornecedor.tipo_entidade === 'PF'}
                onChange={() => setNewFornecedor(prev => ({ ...prev, tipo_entidade: 'PF' }))}
              />
              <label className="form-check-label" htmlFor="quickFornecedorPF">
                Pessoa Física
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="tipoEntidadeFornecedor"
                id="quickFornecedorPJ"
                checked={newFornecedor.tipo_entidade === 'PJ'}
                onChange={() => setNewFornecedor(prev => ({ ...prev, tipo_entidade: 'PJ' }))}
              />
              <label className="form-check-label" htmlFor="quickFornecedorPJ">
                Pessoa Jurídica
              </label>
            </div>
          </div>
        </div>

        {/* Nome ou Razão Social */}
        {newFornecedor.tipo_entidade === 'PF' ? (
          <>
            <div className="col-md-6 mb-3">
              <label className="form-label">Nome <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={newFornecedor.nome || ''}
                onChange={(e) => setNewFornecedor(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Digite o nome completo"
                autoFocus
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">CPF</label>
              <MaskedInput
                value={newFornecedor.cpf || ''}
                onChange={(value) => setNewFornecedor(prev => ({ ...prev, cpf: value }))}
                mask="cpf"
                placeholder="000.000.000-00"
              />
            </div>
          </>
        ) : (
          <>
            <div className="col-md-6 mb-3">
              <label className="form-label">Razão Social <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={newFornecedor.razao_social || ''}
                onChange={(e) => setNewFornecedor(prev => ({ ...prev, razao_social: e.target.value }))}
                placeholder="Nome empresarial"
                autoFocus
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Nome Fantasia</label>
              <input
                type="text"
                className="form-control"
                value={newFornecedor.nome_fantasia || ''}
                onChange={(e) => setNewFornecedor(prev => ({ ...prev, nome_fantasia: e.target.value }))}
                placeholder="Nome comercial"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">CNPJ</label>
              <MaskedInput
                value={newFornecedor.cnpj || ''}
                onChange={(value) => setNewFornecedor(prev => ({ ...prev, cnpj: value }))}
                mask="cnpj"
                placeholder="00.000.000/0000-00"
              />
            </div>
          </>
        )}

        {/* Email */}
        <div className="col-md-6 mb-3">
          <label className="form-label">Email <span className="text-danger">*</span></label>
          <input
            type="email"
            className="form-control"
            value={newFornecedor.email || ''}
            onChange={(e) => setNewFornecedor(prev => ({ ...prev, email: e.target.value }))}
            placeholder="email@exemplo.com"
          />
        </div>

        {/* Telefone */}
        <div className="col-md-6 mb-3">
          <label className="form-label">Telefone <span className="text-danger">*</span></label>
          <MaskedInput
            value={newFornecedor.telefone || ''}
            onChange={(value) => setNewFornecedor(prev => ({ ...prev, telefone: value }))}
            mask="phone"
            placeholder="(00) 00000-0000"
          />
        </div>

        {/* Localização */}
        <div className="col-12 mb-3">
          <label className="form-label mb-0">Localização</label>
          <div className="text-muted small mb-2">
            Cidade e estado do fornecedor
          </div>
          <LocationSelector
            selectedState={newFornecedor.estado}
            selectedMunicipality={newFornecedor.id_municipio ? Number(newFornecedor.id_municipio) : undefined}
            onStateChange={(estado) => {
              setNewFornecedor(prev => ({
                ...prev,
                estado: estado,
                id_municipio: undefined
              }));
            }}
            onMunicipalityChange={(municipioId) => {
              setNewFornecedor(prev => ({
                ...prev,
                id_municipio: Number(municipioId)
              }));
            }}
            apiService={apiService}
          />
        </div>

        {/* Botões de Ação */}
        <div className="col-12">
          <div className="d-flex gap-2 justify-content-end pt-2 border-top">
            <button
              type="button"
              className="btn btn-light"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <i className="ti ti-x me-1"></i>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Salvando...
                </>
              ) : (
                <>
                  <i className="ti ti-check me-1"></i>
                  Salvar Fornecedor
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickFornecedorForm;
